"use client";
import clsx from "clsx";
import { useCallback, useEffect, useRef } from "react";

/**
 * ViewportHeightWrapper - Viewport yüksekliğine göre dinamik max-height ayarlayan jenerik wrapper component
 * 
 * @param {React.ReactNode} children - İçerik
 * @param {boolean} enabled - Max-height hesaplamasını aktif/pasif eder (varsayılan: true)
 * @param {number} defaultMaxHeight - Varsayılan maksimum yükseklik (px)
 * @param {number} bottomPadding - Alttan boşluk (px, varsayılan: 16)
 * @param {string} className - Ek CSS class'ları
 */
const ViewportHeightWrapper = ({
  children,
  enabled = false,
  defaultMaxHeight = 525,
  bottomPadding = 16,
  className = "",
}) => {
  const containerRef = useRef(null);

  const setMaxHeight = useCallback(() => {
    if (!containerRef.current || !enabled) return;

    const rect = containerRef.current.getBoundingClientRect();
    const offsetTop = rect.top; // viewport'a göre üst boşluk
    const viewportHeight = window.innerHeight;

    const availableHeight = viewportHeight - offsetTop - bottomPadding;

    const maxHeight = Math.min(defaultMaxHeight, availableHeight);

    containerRef.current.style.maxHeight = `${maxHeight}px`;
    containerRef.current.style.overflowY = "auto";
  }, [enabled, defaultMaxHeight, bottomPadding]);

  useEffect(() => {
    if (!enabled) return;

    // İlk yüklemede
    setMaxHeight();

    // Resize event listener
    window.addEventListener("resize", setMaxHeight);
    window.addEventListener("scroll", setMaxHeight);

    // Cleanup
    return () => {
      window.removeEventListener("resize", setMaxHeight);
      window.removeEventListener("scroll", setMaxHeight);
    };
  }, [enabled, setMaxHeight]);

  // Container görünür olduğunda tekrar hesapla (animasyon sonrası için)
  useEffect(() => {
    if (!enabled) return;

    const observer = new MutationObserver(() => {
      // DOM değişikliklerinde tekrar hesapla
      setTimeout(setMaxHeight, 100);
    });

    if (containerRef.current) {
      observer.observe(containerRef.current, {
        attributes: true,
        attributeFilter: ["style", "class"],
        childList: true,
        subtree: true,
      });
    }

    return () => {
      observer.disconnect();
    };
  }, [enabled, setMaxHeight]);

  if (!enabled) {
    return children;
  }

  return (
    <div ref={containerRef} className={clsx("scrollbar-hide", className)}>
      {children}
    </div>
  );
};

export default ViewportHeightWrapper;
